import React, { useState, useMemo } from 'react';
import Input from './Input';

// Common country codes (focusing on Antigua & Barbuda and common ones)
const COUNTRY_CODES = [
  { code: '+1', country: 'AG', name: 'Antigua & Barbuda', flag: '🇦🇬' },
  { code: '+1', country: 'US', name: 'United States', flag: '🇺🇸' },
  { code: '+1', country: 'CA', name: 'Canada', flag: '🇨🇦' },
  { code: '+44', country: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: '+33', country: 'FR', name: 'France', flag: '🇫🇷' },
  { code: '+49', country: 'DE', name: 'Germany', flag: '🇩🇪' },
  { code: '+91', country: 'IN', name: 'India', flag: '🇮🇳' },
  { code: '+86', country: 'CN', name: 'China', flag: '🇨🇳' },
  { code: '+81', country: 'JP', name: 'Japan', flag: '🇯🇵' },
  { code: '+61', country: 'AU', name: 'Australia', flag: '🇦🇺' },
];

interface PhoneInputProps {
  value: string;
  onChange: (value: string, countryCode: string) => void;
  countryCode?: string;
  phoneNumber?: string;
  label?: string;
  error?: string;
  helperText?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  required?: boolean;
  verified?: boolean;
  onVerify?: () => void;
}

const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  countryCode: initialCountryCode = '+1',
  phoneNumber: initialPhoneNumber = '',
  label = 'Phone Number',
  error,
  helperText,
  disabled = false,
  fullWidth = false,
  required = false,
  verified = false,
  onVerify,
}) => {
  const [selectedCountryCode, setSelectedCountryCode] = useState(initialCountryCode);
  const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber || value.replace(initialCountryCode, '').trim());
  const [showCountrySelector, setShowCountrySelector] = useState(false);

  // Parse value if it's a full phone number
  useMemo(() => {
    if (value && !phoneNumber) {
      const matched = COUNTRY_CODES.find(cc => value.startsWith(cc.code));
      if (matched) {
        setSelectedCountryCode(matched.code);
        setPhoneNumber(value.replace(matched.code, '').trim());
      }
    }
  }, [value]);

  const handleCountryCodeChange = (code: string) => {
    setSelectedCountryCode(code);
    setShowCountrySelector(false);
    const fullNumber = `${code}${phoneNumber}`;
    onChange(fullNumber, code);
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow digits, spaces, dashes, and parentheses
    const cleaned = e.target.value.replace(/[^\d\s\-()]/g, '');
    setPhoneNumber(cleaned);
    const fullNumber = `${selectedCountryCode}${cleaned}`;
    onChange(fullNumber, selectedCountryCode);
  };

  const selectedCountry = COUNTRY_CODES.find(cc => cc.code === selectedCountryCode) || COUNTRY_CODES[0];
  const displayValue = phoneNumber || '';

  return (
    <div className={fullWidth ? 'w-full' : ''}>
      {label && (
        <label className="block text-sm font-medium text-[var(--color-default-font)] mb-1">
          {label}
          {required && <span className="text-[var(--color-error-600)] ml-1">*</span>}
        </label>
      )}
      <div className="flex gap-2">
        {/* Country Code Selector */}
        <div className="relative">
          <button
            type="button"
            onClick={() => !disabled && setShowCountrySelector(!showCountrySelector)}
            disabled={disabled}
            className={`
              flex items-center gap-1 px-3 py-2 border rounded-md shadow-sm
              bg-[var(--color-default-background)] text-[var(--color-default-font)]
              focus:outline-none focus:ring-2 focus:ring-[var(--color-brand-500)] focus:border-[var(--color-brand-500)]
              disabled:bg-[var(--color-neutral-100)] disabled:cursor-not-allowed
              ${error ? 'border-[var(--color-error-600)]' : 'border-[var(--color-neutral-border)]'}
              min-w-[100px]
            `}
          >
            <span>{selectedCountry.flag}</span>
            <span className="text-sm">{selectedCountry.code}</span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {showCountrySelector && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowCountrySelector(false)}
              />
              <div className="absolute top-full left-0 mt-1 bg-[var(--color-default-background)] border border-[var(--color-neutral-border)] rounded-md shadow-lg z-20 max-h-60 overflow-y-auto min-w-[200px]">
                {COUNTRY_CODES.map((country) => (
                  <button
                    key={country.country}
                    type="button"
                    onClick={() => handleCountryCodeChange(country.code)}
                    className={`
                      w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[var(--color-neutral-100)]
                      ${selectedCountryCode === country.code ? 'bg-[var(--color-brand-50)]' : ''}
                    `}
                  >
                    <span>{country.flag}</span>
                    <span className="text-sm">{country.code}</span>
                    <span className="text-sm text-[var(--color-subtext-color)] ml-auto">{country.name}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Phone Number Input */}
        <div className="flex-1 relative">
          <input
            type="tel"
            value={displayValue}
            onChange={handlePhoneNumberChange}
            disabled={disabled}
            placeholder="123 456 7890"
            className={`
              block w-full px-3 py-2 border rounded-md shadow-sm
              bg-[var(--color-default-background)] text-[var(--color-default-font)]
              focus:outline-none focus:ring-2 focus:ring-[var(--color-brand-500)] focus:border-[var(--color-brand-500)]
              disabled:bg-[var(--color-neutral-100)] disabled:cursor-not-allowed
              ${error ? 'border-[var(--color-error-600)] focus:ring-[var(--color-error-500)] focus:border-[var(--color-error-600)]' : 'border-[var(--color-neutral-border)]'}
            `}
          />
          {verified && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
              <svg className="w-4 h-4 text-[var(--color-success-600)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-xs text-[var(--color-success-600)]">Verified</span>
            </div>
          )}
        </div>
      </div>
      {!verified && onVerify && (
        <div className="mt-1 flex items-center gap-2">
          <span className="text-xs text-[var(--color-warning-600)]">Unverified</span>
          <button
            type="button"
            onClick={onVerify}
            className="text-xs text-[var(--color-brand-primary)] hover:underline"
          >
            Verify Phone
          </button>
        </div>
      )}
      {error && (
        <p className="mt-1 text-sm text-[var(--color-error-600)]" role="alert">
          {error}
        </p>
      )}
      {helperText && !error && (
        <p className="mt-1 text-sm text-[var(--color-subtext-color)]">
          {helperText}
        </p>
      )}
    </div>
  );
};

export default PhoneInput;

